// packages/shared/src/country-config.ts
// PAKT — Configuração de países v2.0
// 31 países, todos com config completa em backend e frontend.
// NÃO alterar os campos legais sem consultar assessoria jurídica local.

export type PaymentMethodType =
  | 'pix'           // BR — EfiBank
  | 'upi'           // IN — Razorpay
  | 'sepa'          // DE/FR/IT/ES/NL/BE/PT/RO — Stripe SEPA
  | 'ideal'         // NL — Stripe iDEAL (submethod do SEPA)
  | 'faster_pay'    // GB — Stripe Open Banking
  | 'interac'       // CA — Stripe ACSS Debit
  | 'npp'           // AU/NZ — Stripe AU BECS
  | 'spei'          // MX — Conekta
  | 'twint'         // CH — Datatrans
  | 'bizum'         // ES — mock (Stripe fallback em prod)
  | 'paypay'        // JP — Stripe JP
  | 'paynow'        // SG — Stripe SG
  | 'swish'         // SE — Stripe SE
  | 'blik'          // PL — Stripe PL
  | 'bancontact'    // BE — Stripe
  | 'promptpay'     // TH — Opn Payments (Omise)
  | 'nip_ng'        // NG — Paystack
  | 'instapay_ph'   // PH — PayMongo
  | 'eft_za'        // ZA — Ozow
  | 'mercadopago'   // AR — Mercado Pago
  | 'webpay'        // CL — Transbank
  | 'duitnow'       // MY — Stripe MY / FPX
  | 'pse'           // CO — Wompi
  | 'bit_il'        // IL — Cardcom (mock em dev)
  | 'credit_card'   // Fallback universal — Stripe

export interface CountryPaymentConfig {
  primary: PaymentMethodType
  fallback: 'credit_card'
  providerName: string
  sandboxAvailable: boolean
  identityStrength: 'strong' | 'medium' | 'weak'
  collectedFields: string[]
  ownAccountWarning: boolean
}

export interface NationalIdConfig {
  fieldName: string
  type: string
  format: RegExp
  validate: boolean
  validatorFn?: string
  required: boolean
  sensitive: boolean
}

export interface CountryConfig {
  code: string
  nameLocal: string
  nameEn: string
  flag: string
  currency: string
  signaturePriceLocal: number
  signaturePriceIsDynamic?: boolean
  locale: string
  rtl: boolean
  lawName: string
  signatureLevel: 'minimalist' | 'advanced' | 'qualified' | 'prescriptive'
  requiresThirdPartyCert: boolean
  payment: CountryPaymentConfig
  nationalId?: NationalIdConfig
  selfieRequired: boolean
  selfieRecommended: boolean
  selfieConsentText: string
  excludedDocumentTypes: string[]
  auditTrailText: string
  auditTrailTextLocal: string
  dataResidencyRequired: boolean
  launchStatus: 'active' | 'beta' | 'requires_partner' | 'restricted'
  warningLevel: 'none' | 'low' | 'medium' | 'high'
  warningText?: string
}

export const VOLATILE_CURRENCIES = ['ARS', 'NGN'] as const

export const COUNTRY_CONFIGS: Record<string, CountryConfig> = {

  BR: {
    code: 'BR', nameLocal: 'Brasil', nameEn: 'Brazil', flag: '🇧🇷',
    currency: 'BRL', signaturePriceLocal: 3.9, locale: 'pt-BR', rtl: false,
    lawName: 'Lei nº 14.063/2020 + MP 2.200-2/2001',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: {
      primary: 'pix', fallback: 'credit_card',
      providerName: 'EfiBank (Gerencianet)',
      sandboxAvailable: true, identityStrength: 'strong',
      collectedFields: ['txid','endToEndId','pagadorNome','pagadorCpfCnpj','bancoOrigem'],
      ownAccountWarning: true,
    },
    nationalId: {
      fieldName: 'CPF', type: 'cpf',
      format: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
      validate: true, validatorFn: 'validateCPF',
      required: false, sensitive: true,
    },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'Ao tirar esta selfie, você declara, sob as penas da lei, ser a pessoa identificada neste documento (Lei 14.063/2020).',
    excludedDocumentTypes: ['escritura_publica','testamento','casamento','adocao'],
    auditTrailText: 'Advanced electronic signature pursuant to Brazilian Law No. 14,063/2020 and MP 2,200-2/2001.',
    auditTrailTextLocal: 'Assinatura eletrônica avançada nos termos da Lei nº 14.063/2020 e MP 2.200-2/2001.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  US: {
    code: 'US', nameLocal: 'United States', nameEn: 'United States', flag: '🇺🇸',
    currency: 'USD', signaturePriceLocal: 1.49, locale: 'en-US', rtl: false,
    lawName: 'ESIGN Act (15 U.S.C. § 7001) + UETA',
    signatureLevel: 'minimalist', requiresThirdPartyCert: false,
    payment: {
      primary: 'faster_pay', fallback: 'credit_card',
      providerName: 'Stripe (Pay by Bank / Plaid)',
      sandboxAvailable: true, identityStrength: 'medium',
      collectedFields: ['accountHolderName','institutionName','accountLast4','routingNumber','plaidAccountId'],
      ownAccountWarning: true,
    },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'By taking this selfie, you confirm your identity for this electronic signature pursuant to the ESIGN Act.',
    excludedDocumentTypes: ['will','testamentary_trust','adoption','divorce','court_order','utility_cancellation','insurance_cancellation'],
    auditTrailText: 'Electronic signature pursuant to the ESIGN Act (15 U.S.C. § 7001) and applicable state UETA.',
    auditTrailTextLocal: 'Electronic signature pursuant to the ESIGN Act (15 U.S.C. § 7001) and applicable state UETA.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  DE: {
    code: 'DE', nameLocal: 'Deutschland', nameEn: 'Germany', flag: '🇩🇪',
    currency: 'EUR', signaturePriceLocal: 1.29, locale: 'de', rtl: false,
    lawName: 'eIDAS (EU) Nr. 910/2014 + VDG + BGB §126a',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'sepa', fallback: 'credit_card', providerName: 'Stripe SEPA Debit', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['iban4','bic','accountHolderName','mandateRef'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'Mit dieser Aufnahme bestätigen Sie Ihre Identität gemäß eIDAS-Verordnung.',
    excludedDocumentTypes: ['grundstueckskauf','erbschaftsvertrag','buergschaft_verbraucher','arbeitsvertrag_befristet_qes'],
    auditTrailText: 'Advanced electronic signature (AES) pursuant to eIDAS Regulation (EU) No 910/2014 and German VDG.',
    auditTrailTextLocal: 'Fortgeschrittene elektronische Signatur gemäß eIDAS-Verordnung (EU) Nr. 910/2014 und VDG.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'medium',
    warningText: 'For documents requiring "Schriftform" under German BGB §126 (e.g. fixed-term employment contracts, surety agreements), a Qualified Electronic Signature (QES) is required. PAKT provides Advanced Electronic Signature (AES).',
  },

  GB: {
    code: 'GB', nameLocal: 'United Kingdom', nameEn: 'United Kingdom', flag: '🇬🇧',
    currency: 'GBP', signaturePriceLocal: 1.19, locale: 'en-GB', rtl: false,
    lawName: 'Electronic Communications Act 2000 (c.7) + UK eIDAS (SI 2016/696)',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'faster_pay', fallback: 'credit_card', providerName: 'Stripe (UK Open Banking)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['sortCode4','accountName','fpsTxnId'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'By capturing this selfie, you confirm your identity for this electronic signature under the Electronic Communications Act 2000.',
    excludedDocumentTypes: ['will','deed_unwitnessed','hmlr_registration'],
    auditTrailText: 'Electronic signature pursuant to the Electronic Communications Act 2000 (c.7) and UK eIDAS Regulations (SI 2016/696).',
    auditTrailTextLocal: 'Electronic signature pursuant to the Electronic Communications Act 2000 (c.7) and UK eIDAS Regulations (SI 2016/696).',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'low',
    warningText: 'For deeds under English law, a witness is required. Add a witness signer to satisfy this requirement.',
  },

  FR: {
    code: 'FR', nameLocal: 'France', nameEn: 'France', flag: '🇫🇷',
    currency: 'EUR', signaturePriceLocal: 1.29, locale: 'fr', rtl: false,
    lawName: 'Règlement eIDAS (UE) n° 910/2014 + Code civil art. 1366-1367',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'sepa', fallback: 'credit_card', providerName: 'Stripe SEPA', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['iban4','accountHolderName','mandateRef'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'En prenant ce selfie, vous confirmez votre identité conformément au règlement eIDAS.',
    excludedDocumentTypes: ['acte_authentique','vente_immeuble','testament'],
    auditTrailText: "Signature électronique avancée conformément au Règlement eIDAS (UE) n° 910/2014 et à l'article 1367 du Code civil.",
    auditTrailTextLocal: "Signature électronique avancée conformément au Règlement eIDAS (UE) n° 910/2014 et à l'article 1367 du Code civil.",
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'low',
  },

  IT: {
    code: 'IT', nameLocal: 'Italia', nameEn: 'Italy', flag: '🇮🇹',
    currency: 'EUR', signaturePriceLocal: 0.99, locale: 'it', rtl: false,
    lawName: 'eIDAS (UE) n. 910/2014 + CAD (D.Lgs. 82/2005)',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'sepa', fallback: 'credit_card', providerName: 'Stripe SEPA', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['iban4','accountHolderName','codiceFiscale','mandateRef'], ownAccountWarning: true },
    nationalId: { fieldName: 'Codice Fiscale', type: 'cf', format: /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i, validate: true, validatorFn: 'validateCodiceFiscale', required: false, sensitive: false },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'Scattando questo selfie, confermi la tua identità ai sensi del Regolamento eIDAS.',
    excludedDocumentTypes: ['atto_notarile','compravendita_immobile','testamento'],
    auditTrailText: 'Firma elettronica avanzata (FEA) ai sensi del Regolamento eIDAS (UE) n. 910/2014 e del CAD (D.Lgs. n. 82/2005).',
    auditTrailTextLocal: 'Firma elettronica avanzata (FEA) ai sensi del Regolamento eIDAS (UE) n. 910/2014 e del CAD (D.Lgs. n. 82/2005).',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'medium',
    warningText: 'Italy distinguishes between FES, FEA, and FEQ. PAKT provides FEA (Advanced). For higher evidentiary weight, a Qualified Electronic Signature (FEQ) via Italian TSP is recommended.',
  },

  ES: {
    code: 'ES', nameLocal: 'España', nameEn: 'Spain', flag: '🇪🇸',
    currency: 'EUR', signaturePriceLocal: 0.99, locale: 'es', rtl: false,
    lawName: 'Reglamento eIDAS (UE) nº 910/2014 + Ley 59/2003 de Firma Electrónica',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'bizum', fallback: 'credit_card', providerName: 'Stripe (Bizum mock em dev)', sandboxAvailable: false, identityStrength: 'medium', collectedFields: ['telefone','nombreTitular','bizumRef'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'Al tomar este selfie, confirma su identidad conforme al Reglamento eIDAS.',
    excludedDocumentTypes: ['escritura_notarial','testamento','matrimonio'],
    auditTrailText: 'Firma electrónica avanzada conforme al Reglamento eIDAS (UE) nº 910/2014 y la Ley 59/2003 de Firma Electrónica.',
    auditTrailTextLocal: 'Firma electrónica avanzada conforme al Reglamento eIDAS (UE) nº 910/2014 y la Ley 59/2003 de Firma Electrónica.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  NL: {
    code: 'NL', nameLocal: 'Nederland', nameEn: 'Netherlands', flag: '🇳🇱',
    currency: 'EUR', signaturePriceLocal: 1.29, locale: 'nl', rtl: false,
    lawName: 'eIDAS Verordening (EU) nr. 910/2014 + BW art. 3:15a',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'ideal', fallback: 'credit_card', providerName: 'Stripe iDEAL', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['iban4','accountHolderName','idealTransactionId','bank'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'Door dit selfie te maken, bevestigt u uw identiteit conform de eIDAS-verordening.',
    excludedDocumentTypes: ['notariele_akte','onroerend_goed','testament'],
    auditTrailText: 'Geavanceerde elektronische handtekening conform eIDAS Verordening (EU) nr. 910/2014 en art. 3:15a BW.',
    auditTrailTextLocal: 'Geavanceerde elektronische handtekening conform eIDAS Verordening (EU) nr. 910/2014 en art. 3:15a BW.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  BE: {
    code: 'BE', nameLocal: 'België / Belgique', nameEn: 'Belgium', flag: '🇧🇪',
    currency: 'EUR', signaturePriceLocal: 0.99, locale: 'fr-BE', rtl: false,
    lawName: 'Règlement eIDAS (UE) n° 910/2014 + Loi du 21 juillet 2016',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'bancontact', fallback: 'credit_card', providerName: 'Stripe Bancontact', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['iban4','accountHolderName','bancontactRef'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: "En prenant ce selfie, vous confirmez votre identité conformément au règlement eIDAS (UE) n° 910/2014.",
    excludedDocumentTypes: ['acte_notarie','testament','hypotheque','mariage'],
    auditTrailText: 'Signature électronique avancée conformément au Règlement eIDAS (UE) n° 910/2014 et à la Loi belge du 21 juillet 2016.',
    auditTrailTextLocal: 'Geavanceerde elektronische handtekening conform eIDAS Verordening (EU) nr. 910/2014 en de Belgische Wet van 21 juli 2016.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  PT: {
    code: 'PT', nameLocal: 'Portugal', nameEn: 'Portugal', flag: '🇵🇹',
    currency: 'EUR', signaturePriceLocal: 0.79, locale: 'pt-PT', rtl: false,
    lawName: 'Regulamento eIDAS (UE) n.º 910/2014 + D.L. n.º 290-D/99',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'sepa', fallback: 'credit_card', providerName: 'Stripe SEPA Debit', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['iban4','accountHolderName','mandateRef'], ownAccountWarning: true },
    nationalId: { fieldName: 'NIF', type: 'nif_pt', format: /^\d{9}$/, validate: true, validatorFn: 'validateNIF', required: false, sensitive: false },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'Ao tirar esta selfie, confirma a sua identidade para esta assinatura eletrónica nos termos do Regulamento eIDAS e do D.L. n.º 290-D/99.',
    excludedDocumentTypes: ['escritura_notarial','testamento','casamento','adocao'],
    auditTrailText: 'Assinatura eletrónica avançada nos termos do Regulamento eIDAS (UE) n.º 910/2014 e do Decreto-Lei n.º 290-D/99.',
    auditTrailTextLocal: 'Assinatura eletrónica avançada nos termos do Regulamento eIDAS (UE) n.º 910/2014 e do Decreto-Lei n.º 290-D/99.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  RO: {
    code: 'RO', nameLocal: 'România', nameEn: 'Romania', flag: '🇷🇴',
    currency: 'RON', signaturePriceLocal: 4.9, locale: 'ro', rtl: false,
    lawName: 'Regulamentul eIDAS (UE) nr. 910/2014 + Legea nr. 455/2001',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'sepa', fallback: 'credit_card', providerName: 'Stripe Romania (SEPA / card)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['iban4','accountHolderName','mandateRef'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'Prin capturarea acestui selfie, vă confirmați identitatea pentru această semnătură electronică în conformitate cu Regulamentul eIDAS.',
    excludedDocumentTypes: ['act_notarial','testament','contract_vanzare_imobil'],
    auditTrailText: 'Semnătură electronică avansată în conformitate cu Regulamentul eIDAS (UE) nr. 910/2014 și Legea nr. 455/2001.',
    auditTrailTextLocal: 'Semnătură electronică avansată în conformitate cu Regulamentul eIDAS (UE) nr. 910/2014 și Legea nr. 455/2001.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  CH: {
    code: 'CH', nameLocal: 'Schweiz / Suisse / Svizzera', nameEn: 'Switzerland', flag: '🇨🇭',
    currency: 'CHF', signaturePriceLocal: 1.4, locale: 'de-CH', rtl: false,
    lawName: 'ZertES (SR 943.03) + OR art. 14',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'twint', fallback: 'credit_card', providerName: 'Datatrans (TWINT)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['twintTransactionRef','accountHolderName','bankOrigin'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'Mit dieser Aufnahme bestätigen Sie Ihre Identität gemäß ZertES.',
    excludedDocumentTypes: ['grundstueckkauf','testament','familienrecht'],
    auditTrailText: 'Electronic signature pursuant to Swiss ZertES (SR 943.03) and OR art. 14.',
    auditTrailTextLocal: 'Elektronische Signatur gemäß ZertES (SR 943.03) und Art. 14 OR.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'low',
    warningText: 'Switzerland is not an EU member; eIDAS does not apply. ZertES governs. For documents requiring "Schriftlichkeit" under OR art. 14, a QES is required.',
  },

  CA: {
    code: 'CA', nameLocal: 'Canada', nameEn: 'Canada', flag: '🇨🇦',
    currency: 'CAD', signaturePriceLocal: 1.49, locale: 'en-CA', rtl: false,
    lawName: 'Uniform Electronic Commerce Act (1999) + provincial e-commerce acts',
    signatureLevel: 'minimalist', requiresThirdPartyCert: false,
    payment: { primary: 'interac', fallback: 'credit_card', providerName: 'Stripe ACSS Debit (Interac)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['accountHolderName','bankId','interacRef'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'By capturing this selfie, you confirm your identity for this electronic signature under applicable Canadian electronic commerce legislation.',
    excludedDocumentTypes: ['will','real_estate_transfer'],
    auditTrailText: 'Electronic signature pursuant to applicable provincial electronic commerce legislation (UECA model / LCCJTI for Québec).',
    auditTrailTextLocal: 'Electronic signature pursuant to applicable provincial electronic commerce legislation (UECA model / LCCJTI for Québec).',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'low',
    warningText: 'Québec residents: interface and documents are available in French. The LCCJTI applies in Québec.',
  },

  AU: {
    code: 'AU', nameLocal: 'Australia', nameEn: 'Australia', flag: '🇦🇺',
    currency: 'AUD', signaturePriceLocal: 1.49, locale: 'en-AU', rtl: false,
    lawName: 'Electronic Transactions Act 1999 (Cth) + Corporations Act 2001',
    signatureLevel: 'minimalist', requiresThirdPartyCert: false,
    payment: { primary: 'npp', fallback: 'credit_card', providerName: 'Stripe Australia (BECS / PayID)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['payId','accountHolderName','nppTransactionId'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'By capturing this selfie, you confirm your identity for this electronic signature under the Electronic Transactions Act 1999.',
    excludedDocumentTypes: ['will','real_property_conveyance'],
    auditTrailText: 'Electronic signature pursuant to the Electronic Transactions Act 1999 (Cth) and applicable state/territory legislation.',
    auditTrailTextLocal: 'Electronic signature pursuant to the Electronic Transactions Act 1999 (Cth) and applicable state/territory legislation.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  NZ: {
    code: 'NZ', nameLocal: 'New Zealand', nameEn: 'New Zealand', flag: '🇳🇿',
    currency: 'NZD', signaturePriceLocal: 1.69, locale: 'en-NZ', rtl: false,
    lawName: 'Contract and Commercial Law Act 2017 (Part 4)',
    signatureLevel: 'minimalist', requiresThirdPartyCert: false,
    payment: { primary: 'npp', fallback: 'credit_card', providerName: 'Stripe New Zealand', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['accountHolderName','bankCode','nzTransactionRef'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'By capturing this selfie, you confirm your identity for this electronic signature under the Contract and Commercial Law Act 2017.',
    excludedDocumentTypes: ['will','property_sale_agreement','enduring_power_of_attorney'],
    auditTrailText: 'Electronic signature pursuant to the Contract and Commercial Law Act 2017 (Part 4), New Zealand.',
    auditTrailTextLocal: 'Electronic signature pursuant to the Contract and Commercial Law Act 2017 (Part 4), New Zealand.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  MX: {
    code: 'MX', nameLocal: 'México', nameEn: 'Mexico', flag: '🇲🇽',
    currency: 'MXN', signaturePriceLocal: 18.0, locale: 'es-MX', rtl: false,
    lawName: 'Código de Comercio arts. 89-114 + NOM-151-SCFI-2016',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'spei', fallback: 'credit_card', providerName: 'Conekta (SPEI/CoDi)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['clabe4','nombreCuentahabiente','referenciaSpei'], ownAccountWarning: true },
    nationalId: { fieldName: 'RFC', type: 'rfc', format: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i, validate: true, validatorFn: 'validateRFC', required: false, sensitive: false },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'Al tomar este selfie, confirma su identidad para esta firma electrónica conforme al Código de Comercio.',
    excludedDocumentTypes: ['escritura_notarial','transmision_inmueble'],
    auditTrailText: 'Firma electrónica conforme al Código de Comercio (arts. 89-114) y NOM-151-SCFI-2016.',
    auditTrailTextLocal: 'Firma electrónica conforme al Código de Comercio (arts. 89-114) y NOM-151-SCFI-2016.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'medium',
    warningText: 'Para plena validez bajo NOM-151-SCFI-2016, se requiere constancia de conservación de PSC autorizado por el SAT. PAKT proporciona firma electrónica válida bajo el Código de Comercio.',
  },

  CO: {
    code: 'CO', nameLocal: 'Colombia', nameEn: 'Colombia', flag: '🇨🇴',
    currency: 'COP', signaturePriceLocal: 3900.0, locale: 'es-CO', rtl: false,
    lawName: 'Ley 527/1999 (UNCITRAL model) + Ley 2213/2022',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'pse', fallback: 'credit_card', providerName: 'Wompi (PSE / Bancolombia)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['wompiTransactionId','payerName','legalId','bankName'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'Al tomar este selfie, confirma su identidad para esta firma electrónica conforme a la Ley 527/1999.',
    excludedDocumentTypes: ['escritura_publica','hipoteca','testamento'],
    auditTrailText: 'Firma electrónica conforme a la Ley 527/1999 sobre Comercio Electrónico y la Ley 2213/2022.',
    auditTrailTextLocal: 'Firma electrónica conforme a la Ley 527/1999 sobre Comercio Electrónico y la Ley 2213/2022.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  AR: {
    code: 'AR', nameLocal: 'Argentina', nameEn: 'Argentina', flag: '🇦🇷',
    currency: 'ARS', signaturePriceLocal: 1.00, signaturePriceIsDynamic: true,
    locale: 'es-AR', rtl: false,
    lawName: 'Ley 25.506/2001 de Firma Digital + Decreto 2628/2002',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'mercadopago', fallback: 'credit_card', providerName: 'Mercado Pago (Transferências 3.0)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['mpPaymentId','payerName','payerDocType','payerDocNumber'], ownAccountWarning: true },
    nationalId: { fieldName: 'CUIL / CUIT', type: 'cuil_ar', format: /^\d{2}-\d{8}-\d$/, validate: false, required: false, sensitive: true },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'Al tomar este selfie, confirma su identidad para esta firma electrónica conforme a la Ley 25.506/2001.',
    excludedDocumentTypes: ['escritura_publica','hipoteca','testamento','acto_notarial'],
    auditTrailText: 'Firma electrónica avanzada conforme a la Ley 25.506/2001 de Firma Digital de la República Argentina.',
    auditTrailTextLocal: 'Firma electrónica avanzada conforme a la Ley 25.506/2001 de Firma Digital de la República Argentina.',
    dataResidencyRequired: false,
    launchStatus: 'beta', warningLevel: 'medium',
    warningText: 'El precio en ARS se actualiza automáticamente vía API de tipo de cambio.',
  },

  CL: {
    code: 'CL', nameLocal: 'Chile', nameEn: 'Chile', flag: '🇨🇱',
    currency: 'CLP', signaturePriceLocal: 890.0, locale: 'es-CL', rtl: false,
    lawName: 'Ley 19.799/2002 sobre Documentos Electrónicos y Firma Electrónica',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'webpay', fallback: 'credit_card', providerName: 'Transbank Webpay Plus', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['webpayToken','authorizationCode','cardType','last4'], ownAccountWarning: true },
    nationalId: { fieldName: 'RUT', type: 'rut_cl', format: /^\d{1,2}\.\d{3}\.\d{3}-[\dkK]$/, validate: true, validatorFn: 'validateRUT', required: false, sensitive: false },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'Al tomar este selfie, confirma su identidad para esta firma electrónica conforme a la Ley 19.799/2002.',
    excludedDocumentTypes: ['escritura_publica','hipoteca','testamento','matrimonio'],
    auditTrailText: 'Firma electrónica avanzada conforme a la Ley 19.799/2002, República de Chile.',
    auditTrailTextLocal: 'Firma electrónica avanzada conforme a la Ley 19.799/2002, República de Chile.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  JP: {
    code: 'JP', nameLocal: '日本', nameEn: 'Japan', flag: '🇯🇵',
    currency: 'JPY', signaturePriceLocal: 149.0, locale: 'ja', rtl: false,
    lawName: '電子署名及び認証業務に関する法律 (Law No. 102/2000)',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'paypay', fallback: 'credit_card', providerName: 'Stripe JP (PayPay)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['payPayUserId','transactionId'], ownAccountWarning: false },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'この自撮り写真は、電子署名法に基づくあなたの本人確認のために使用されます。',
    excludedDocumentTypes: ['fudosan_baibai','igon','koseki'],
    auditTrailText: 'Electronic signature pursuant to Japan Electronic Signatures Act (Law No. 102/2000).',
    auditTrailTextLocal: '本電子署名は電子署名及び認証業務に関する法律（平成12年法律第102号）に基づき有効です。',
    dataResidencyRequired: false,
    launchStatus: 'beta', warningLevel: 'medium',
    warningText: 'Japan module is in beta. PayPay merchant integration requires Japanese business registration for production.',
  },

  IN: {
    code: 'IN', nameLocal: 'India', nameEn: 'India', flag: '🇮🇳',
    currency: 'INR', signaturePriceLocal: 49.0, locale: 'hi', rtl: false,
    lawName: 'Information Technology Act, 2000 (Section 5) + Indian Contract Act, 1872',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'upi', fallback: 'credit_card', providerName: 'Razorpay UPI', sandboxAvailable: true, identityStrength: 'strong', collectedFields: ['upiId','utr','bankName','payerVpa','payerName'], ownAccountWarning: true },
    nationalId: { fieldName: 'PAN', type: 'pan_in', format: /^[A-Z]{5}\d{4}[A-Z]$/, validate: false, required: false, sensitive: true },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'By capturing this selfie, you confirm your identity for this electronic signature pursuant to the Information Technology Act, 2000 (Section 5).',
    excludedDocumentTypes: ['immovable_property','negotiable_instrument','will','power_of_attorney','trust_deed','government_filing','court_document'],
    auditTrailText: 'Electronic signature pursuant to IT Act, 2000 (Section 5) and Indian Contract Act, 1872.',
    auditTrailTextLocal: 'सूचना प्रौद्योगिकी अधिनियम, 2000 (धारा 5) एवं भारतीय संविदा अधिनियम, 1872 के अंतर्गत इलेक्ट्रॉनिक हस्ताक्षर।',
    dataResidencyRequired: false,
    launchStatus: 'beta', warningLevel: 'medium',
    warningText: 'PAKT provides electronic signatures valid for private commercial contracts under IT Act Section 5.',
  },

  SG: {
    code: 'SG', nameLocal: 'Singapore', nameEn: 'Singapore', flag: '🇸🇬',
    currency: 'SGD', signaturePriceLocal: 1.29, locale: 'en-SG', rtl: false,
    lawName: 'Electronic Transactions Act (Cap. 88, 2011 Rev Ed) + PDPA 2012',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'paynow', fallback: 'credit_card', providerName: 'Stripe Singapore (PayNow)', sandboxAvailable: true, identityStrength: 'strong', collectedFields: ['paynowUen','paynowTransactionRef','payerName','bankOrigin'], ownAccountWarning: true },
    nationalId: { fieldName: 'NRIC / FIN', type: 'nric_sg', format: /^[STFG]\d{7}[A-Z]$/i, validate: false, required: false, sensitive: true },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'By capturing this selfie, you confirm your identity for this electronic signature under the Electronic Transactions Act (Cap. 88).',
    excludedDocumentTypes: ['will','power_of_attorney_property','cpf_nomination'],
    auditTrailText: 'Electronic signature pursuant to the Electronic Transactions Act (Cap. 88, 2011 Rev Ed), Singapore.',
    auditTrailTextLocal: 'Electronic signature pursuant to the Electronic Transactions Act (Cap. 88, 2011 Rev Ed), Singapore.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  MY: {
    code: 'MY', nameLocal: 'Malaysia', nameEn: 'Malaysia', flag: '🇲🇾',
    currency: 'MYR', signaturePriceLocal: 3.9, locale: 'ms', rtl: false,
    lawName: 'Electronic Commerce Act 2006 + Digital Signature Act 1997',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'duitnow', fallback: 'credit_card', providerName: 'Stripe MY (DuitNow / FPX)', sandboxAvailable: true, identityStrength: 'strong', collectedFields: ['duitnowRef','icNumberLast4','accountHolderName','bankOrigin'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'Dengan mengambil selfie ini, anda mengesahkan identiti anda untuk tandatangan elektronik ini.',
    excludedDocumentTypes: ['land_transfer','will','court_document'],
    auditTrailText: 'Electronic signature pursuant to the Electronic Commerce Act 2006 and Digital Signature Act 1997, Malaysia.',
    auditTrailTextLocal: 'Tandatangan elektronik mengikut Akta Perdagangan Elektronik 2006 dan Akta Tandatangan Digital 1997, Malaysia.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  TH: {
    code: 'TH', nameLocal: 'ประเทศไทย', nameEn: 'Thailand', flag: '🇹🇭',
    currency: 'THB', signaturePriceLocal: 29.0, locale: 'th', rtl: false,
    lawName: 'Electronic Transactions Act B.E. 2544 (2001, rev. B.E. 2562/2019)',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'promptpay', fallback: 'credit_card', providerName: 'Opn Payments / Omise (PromptPay)', sandboxAvailable: true, identityStrength: 'strong', collectedFields: ['promptPayRef','citizenIdLast4','bankOrigin','payerName'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'การถ่ายภาพเซลฟี่นี้เพื่อยืนยันตัวตนของคุณตามพระราชบัญญัติว่าด้วยธุรกรรมทางอิเล็กทรอนิกส์ พ.ศ. 2544',
    excludedDocumentTypes: ['land_transfer','real_property','will'],
    auditTrailText: 'Electronic signature pursuant to Thailand Electronic Transactions Act B.E. 2544 (2001) as amended by B.E. 2562 (2019).',
    auditTrailTextLocal: 'ลายมือชื่ออิเล็กทรอนิกส์ตามพระราชบัญญัติว่าด้วยธุรกรรมทางอิเล็กทรอนิกส์ พ.ศ. 2544 (แก้ไขเพิ่มเติม พ.ศ. 2562)',
    dataResidencyRequired: false,
    launchStatus: 'beta', warningLevel: 'none',
  },

  SE: {
    code: 'SE', nameLocal: 'Sverige', nameEn: 'Sweden', flag: '🇸🇪',
    currency: 'SEK', signaturePriceLocal: 9.9, locale: 'sv', rtl: false,
    lawName: 'eIDAS (EU) 910/2014 + Lag (2000:832) om kvalificerade elektroniska signaturer',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'swish', fallback: 'credit_card', providerName: 'Stripe SE (Swish)', sandboxAvailable: true, identityStrength: 'strong', collectedFields: ['swishTransactionId','payerAlias','payerName','personnummerLast4'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'Genom att ta detta selfie bekräftar du din identitet för denna elektroniska signatur i enlighet med eIDAS-förordningen.',
    excludedDocumentTypes: ['fastighetsoverlatelese','testamente','aktenskapsforord'],
    auditTrailText: 'Advanced electronic signature pursuant to eIDAS Regulation (EU) No 910/2014 and Swedish Lag (2000:832).',
    auditTrailTextLocal: 'Avancerad elektronisk signatur i enlighet med eIDAS-förordningen (EU) nr 910/2014 och Lag (2000:832).',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  PL: {
    code: 'PL', nameLocal: 'Polska', nameEn: 'Poland', flag: '🇵🇱',
    currency: 'PLN', signaturePriceLocal: 3.9, locale: 'pl', rtl: false,
    lawName: 'eIDAS (UE) 910/2014 + Ustawa o podpisie elektronicznym (2001)',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'blik', fallback: 'credit_card', providerName: 'Stripe PL (BLIK)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['blikTransactionId','accountHolderName','blikIssuerBank'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'Robiąc to zdjęcie, potwierdzasz swoją tożsamość dla tego podpisu elektronicznego zgodnie z rozporządzeniem eIDAS.',
    excludedDocumentTypes: ['akt_notarialny','testament','umowa_o_prace_qes'],
    auditTrailText: 'Zaawansowany podpis elektroniczny zgodnie z rozporządzeniem eIDAS (UE) nr 910/2014.',
    auditTrailTextLocal: 'Zaawansowany podpis elektroniczny zgodnie z rozporządzeniem eIDAS (UE) nr 910/2014.',
    dataResidencyRequired: false,
    launchStatus: 'active', warningLevel: 'none',
  },

  AE: {
    code: 'AE', nameLocal: 'الإمارات العربية المتحدة', nameEn: 'United Arab Emirates', flag: '🇦🇪',
    currency: 'AED', signaturePriceLocal: 3.49, locale: 'ar-AE', rtl: true,
    lawName: 'Federal Law No. 46/2021 (Electronic Transactions) + DIFC Law No. 2',
    signatureLevel: 'minimalist', requiresThirdPartyCert: false,
    payment: { primary: 'credit_card', fallback: 'credit_card', providerName: 'Stripe UAE', sandboxAvailable: true, identityStrength: 'weak', collectedFields: ['last4','cardholderName','fingerprint'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'بالتقاط هذه الصورة الشخصية، تؤكد هويتك لهذا التوقيع الإلكتروني وفقًا للقانون الاتحادي رقم 46/2021.',
    excludedDocumentTypes: ['real_estate','marriage','will','court_document'],
    auditTrailText: 'Electronic signature pursuant to UAE Federal Law No. 46/2021 on Electronic Transactions and Trust Services.',
    auditTrailTextLocal: 'توقيع إلكتروني وفقًا للقانون الاتحادي رقم 46/2021 بشأن المعاملات الإلكترونية وخدمات الثقة.',
    dataResidencyRequired: false,
    launchStatus: 'beta', warningLevel: 'low',
    warningText: 'UAE AANI instant payment integration requires a local banking partner. MVP uses Stripe card as primary.',
  },

  IL: {
    code: 'IL', nameLocal: 'ישראל', nameEn: 'Israel', flag: '🇮🇱',
    currency: 'ILS', signaturePriceLocal: 3.49, locale: 'he', rtl: true,
    lawName: 'Electronic Signature Law 5761-2001 (as amended 5772-2011)',
    signatureLevel: 'advanced', requiresThirdPartyCert: false,
    payment: { primary: 'bit_il', fallback: 'credit_card', providerName: 'Cardcom (Bit) — mock em dev', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['bitTransactionId','payerName','last4'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: false,
    selfieConsentText: 'על ידי צילום הסלפי הזה, אתה מאשר את זהותך לחתימה אלקטרונית זו בהתאם לחוק חתימה אלקטרונית, תשס"א-2001.',
    excludedDocumentTypes: ['tabu_registration','will','marriage_contract'],
    auditTrailText: 'Electronic signature pursuant to the Electronic Signature Law 5761-2001 (as amended 5772-2011), State of Israel.',
    auditTrailTextLocal: 'חתימה אלקטרונית בהתאם לחוק חתימה אלקטרונית, תשס"א-2001 (כפי שתוקן בתשע"ב-2011), מדינת ישראל.',
    dataResidencyRequired: false,
    launchStatus: 'beta', warningLevel: 'low',
    warningText: 'Israeli law distinguishes simple vs secure electronic signatures. PAKT provides the advanced equivalent.',
  },

  NG: {
    code: 'NG', nameLocal: 'Nigeria', nameEn: 'Nigeria', flag: '🇳🇬',
    currency: 'NGN', signaturePriceLocal: 990.0, signaturePriceIsDynamic: true,
    locale: 'en-NG', rtl: false,
    lawName: 'Cybercrimes Act 2015 + Evidence Act 2011 (Section 84)',
    signatureLevel: 'minimalist', requiresThirdPartyCert: false,
    payment: { primary: 'nip_ng', fallback: 'credit_card', providerName: 'Paystack (NIP — NIBSS Instant Payment)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['paystackReference','accountName','bankName','last4Account'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'By capturing this selfie, you confirm your identity for this electronic signature under the Cybercrimes Act 2015 and Evidence Act 2011.',
    excludedDocumentTypes: ['land_title','statutory_declaration','court_affidavit'],
    auditTrailText: 'Electronic signature pursuant to the Nigerian Cybercrimes Act 2015 and the Evidence Act 2011 (Section 84).',
    auditTrailTextLocal: 'Electronic signature pursuant to the Nigerian Cybercrimes Act 2015 and the Evidence Act 2011 (Section 84).',
    dataResidencyRequired: false,
    launchStatus: 'beta', warningLevel: 'low',
    warningText: 'Nigeria does not yet have a standalone Electronic Signatures Act.',
  },

  PH: {
    code: 'PH', nameLocal: 'Pilipinas', nameEn: 'Philippines', flag: '🇵🇭',
    currency: 'PHP', signaturePriceLocal: 49.0, locale: 'en-PH', rtl: false,
    lawName: 'Republic Act No. 8792 (Electronic Commerce Act, 2000)',
    signatureLevel: 'minimalist', requiresThirdPartyCert: false,
    payment: { primary: 'instapay_ph', fallback: 'credit_card', providerName: 'PayMongo (InstaPay / GCash)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['paymongoPaymentId','payerName','paymentMethod'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'By capturing this selfie, you confirm your identity for this electronic signature under Republic Act No. 8792.',
    excludedDocumentTypes: ['real_property_transfer','notarial_will','chattel_mortgage_registration'],
    auditTrailText: 'Electronic signature pursuant to the Philippines Electronic Commerce Act (Republic Act No. 8792, 2000).',
    auditTrailTextLocal: 'Electronic signature pursuant to the Philippines Electronic Commerce Act (Republic Act No. 8792, 2000).',
    dataResidencyRequired: false,
    launchStatus: 'beta', warningLevel: 'low',
    warningText: 'Under Philippine law, notarial acts require a licensed notary public and cannot be executed electronically.',
  },

  ZA: {
    code: 'ZA', nameLocal: 'South Africa', nameEn: 'South Africa', flag: '🇿🇦',
    currency: 'ZAR', signaturePriceLocal: 14.9, locale: 'en-ZA', rtl: false,
    lawName: 'Electronic Communications and Transactions Act 25 of 2002 (ECT Act)',
    signatureLevel: 'minimalist', requiresThirdPartyCert: false,
    payment: { primary: 'eft_za', fallback: 'credit_card', providerName: 'Ozow (EFT instantâneo)', sandboxAvailable: true, identityStrength: 'medium', collectedFields: ['ozowTransactionId','accountHolderName','bankName'], ownAccountWarning: true },
    selfieRequired: false, selfieRecommended: true,
    selfieConsentText: 'By capturing this selfie, you confirm your identity for this electronic signature under the ECT Act 25 of 2002.',
    excludedDocumentTypes: ['alienation_of_land','will','antenuptial_contract'],
    auditTrailText: 'Electronic signature pursuant to the Electronic Communications and Transactions Act 25 of 2002 (ECT Act), South Africa.',
    auditTrailTextLocal: 'Electronic signature pursuant to the Electronic Communications and Transactions Act 25 of 2002 (ECT Act), South Africa.',
    dataResidencyRequired: false,
    launchStatus: 'beta', warningLevel: 'low',
    warningText: 'South African ECT Act Schedule 2 excludes: alienation of land, long-term leases, wills, and antenuptial contracts.',
  },
}

// Helper: lista ordenada para UI
export const COUNTRY_LIST = Object.values(COUNTRY_CONFIGS)
  .sort((a, b) => a.nameEn.localeCompare(b.nameEn))

export const ACTIVE_COUNTRIES = COUNTRY_LIST
  .filter(c => c.launchStatus === 'active')

export const ALL_COUNTRIES = COUNTRY_LIST
  .filter(c => c.launchStatus !== 'restricted')

// Mapeamento país → locale i18n
export const COUNTRY_LOCALE_MAP: Record<string, string> = {
  BR: 'pt-BR', US: 'en-US', MX: 'es-MX', CO: 'es-CO', AR: 'es-AR', CL: 'es-CL', CA: 'en-CA',
  DE: 'de', FR: 'fr', IT: 'it', ES: 'es', NL: 'nl', BE: 'fr-BE', PT: 'pt-PT', RO: 'ro',
  GB: 'en-GB', CH: 'de-CH', SE: 'sv', PL: 'pl',
  AU: 'en-AU', NZ: 'en-NZ',
  JP: 'ja', IN: 'hi', SG: 'en-SG', MY: 'ms', TH: 'th', PH: 'en-PH',
  NG: 'en-NG', ZA: 'en-ZA',
  AE: 'ar-AE', IL: 'he',
}

export const RTL_LOCALES = ['ar', 'ar-AE', 'he'] as const
export const ENGLISH_AS_OFFICIAL = ['US', 'GB', 'AU', 'NZ', 'CA', 'SG', 'PH', 'NG', 'ZA'] as const
